/*
 * Tab Keyboard
 *
 * Keyboard support and selection state for the tab sets @Html.Tabs renders
 * (Infrastructure/Mvc/TabsExtensions.cs), following the WAI-ARIA tab pattern:
 *
 *   - Left and Right arrows move focus between the tabs, wrapping at the ends, and
 *     Home and End jump to the first and last. Moving focus doesn't open anything:
 *     each tab loads its pane over AJAX, so Enter or Space opens the focused tab.
 *   - Opening a tab marks it selected (mt-isActive, aria-selected), makes it the
 *     strip's only Tab stop, and labels the pane with it.
 *
 * Only strips carrying the js-tabs hook are handled, so hand-written mt-toggle tab
 * sets behave exactly as before. Tabs.vue does the same for itself.
 *
 * Clicks and keys are delegated from the document. A tab opened from script needs
 * one more thing: $qs.app.selectTabs opens the initial tab with jQuery's
 * .trigger('click'), which runs jQuery's handlers (qs-common.js adds mt-isActive)
 * but never dispatches a native click. So each strip is also watched for a tab
 * gaining mt-isActive, the same signal tabScroll uses, including strips added
 * later (AJAX partials, panes, modals).
 */

const ACTIVE_CLASS = 'mt-isActive';
const STRIP_SELECTOR = '.js-tabs';
const TAB_SELECTOR = '.js-tabs [role="tab"]';

const watched = new WeakSet();
let selectionObserver = null;

const tabsOf = tab => Array.from(tab.closest('[role="tablist"]').querySelectorAll('[role="tab"]'));

const isSelected = tab => tab.getAttribute('aria-selected') === 'true';

// Marks the tab selected and its siblings not, and points the pane at it. A pane
// passed to .Pane() is the page's own element, so it may need the role too.
function select(tab) {
  tabsOf(tab).forEach(other => {
    const selected = other === tab;
    other.classList.toggle(ACTIVE_CLASS, selected);
    other.setAttribute('aria-selected', String(selected));
    other.setAttribute('tabindex', selected ? '0' : '-1');
  });

  const pane = document.getElementById(tab.getAttribute('aria-controls'));
  if (!pane) {
    return;
  }

  if (!pane.hasAttribute('role')) {
    pane.setAttribute('role', 'tabpanel');
    pane.setAttribute('tabindex', '0');
  }
  pane.setAttribute('aria-labelledby', tab.id);
}

// The tab an arrow, Home or End key moves to, or null for any other key.
function target(tabs, index, key) {
  switch (key) {
    case 'ArrowRight':
      return tabs[(index + 1) % tabs.length];
    case 'ArrowLeft':
      return tabs[(index - 1 + tabs.length) % tabs.length];
    case 'Home':
      return tabs[0];
    case 'End':
      return tabs[tabs.length - 1];
    default:
      return null;
  }
}

function onClick(event) {
  const tab = event.target instanceof Element ? event.target.closest(TAB_SELECTOR) : null;
  if (tab) {
    select(tab);
  }
}

function onKeydown(event) {
  const tab = event.target;
  if (!(tab instanceof Element) || !tab.matches(TAB_SELECTOR)) {
    return;
  }

  if (event.altKey || event.ctrlKey || event.metaKey) {
    return;
  }

  // Enter follows the link natively; Space has to be wired. Neither reloads the
  // pane that is already open, matching a mouse, which can't click the selected tab.
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    if (!isSelected(tab)) {
      tab.click();
    }
    return;
  }

  const tabs = tabsOf(tab);
  const next = target(tabs, tabs.indexOf(tab), event.key);
  if (next) {
    event.preventDefault();
    next.focus();
  }
}

// Selects a tab that something else gave mt-isActive. select() changes classes
// too, but only ever leaves the selected tab active, so it doesn't loop.
function onClassChanges(mutations) {
  new Set(mutations.map(mutation => mutation.target)).forEach(element => {
    if (element.matches('[role="tab"]') && element.classList.contains(ACTIVE_CLASS) && !isSelected(element)) {
      select(element);
    }
  });
}

function watch(strip) {
  const list = strip.querySelector('[role="tablist"]');
  if (!list || watched.has(list)) {
    return;
  }

  watched.add(list);
  selectionObserver.observe(list, { attributes: true, attributeFilter: ['class'], subtree: true });

  // A tab that was already active before the strip was watched.
  const active = list.querySelector(`[role="tab"].${ACTIVE_CLASS}`);
  if (active && !isSelected(active)) {
    select(active);
  }
}

function watchWithin(root) {
  if (root.nodeType !== Node.ELEMENT_NODE) {
    return;
  }
  if (root.matches(STRIP_SELECTOR)) {
    watch(root);
  }
  root.querySelectorAll(STRIP_SELECTOR).forEach(watch);
}

function start() {
  selectionObserver = new MutationObserver(onClassChanges);
  watchWithin(document.body);

  new MutationObserver(mutations => {
    mutations.forEach(mutation => mutation.addedNodes.forEach(watchWithin));
  }).observe(document.body, { childList: true, subtree: true });
}

if (typeof window !== 'undefined') {
  document.addEventListener('click', onClick);
  document.addEventListener('keydown', onKeydown);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
}

export default { select };
